import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Search, IdCard, MessageCircle, BadgeCheck, Pin, Camera, ShieldCheck, GraduationCap, ArrowRight } from "lucide-react";

const NEIGHBOR_STEPS = [
  { icon: Search, title: "Search your neighborhood", desc: "Enter your ZIP and browse pros your neighbors already trust — no account needed." },
  { icon: IdCard, title: "Read their card", desc: "Reviews, past work, credentials, and a real portfolio, all in one place." },
  { icon: MessageCircle, title: "Message directly", desc: "Reach out inside LIMYA. No lead fees, no middleman, no spam." },
  { icon: Pin, title: "Save & organize", desc: "Pin pros to My Board and group them into project folders for later." },
];

const PRO_TIPS = [
  { icon: Camera, title: "Show your work", desc: "Add a portfolio of real projects. Photos build trust faster than words." },
  { icon: BadgeCheck, title: "Get verified", desc: "Verify your email and complete your profile to earn a Verified badge." },
  { icon: ShieldCheck, title: "Collect recommendations", desc: "Neighbors you've worked with can vouch for you right on your card." },
];

export default function Learn() {
  return (
    <div className="bg-[#F5F7F5] min-h-screen">
      <Header/>
      <div className="max-w-5xl mx-auto px-6 py-14" data-testid="learn-page">
        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#E1EDE7] text-[#245544] text-xs font-bold uppercase tracking-wider mb-4">
          <GraduationCap size={13}/> Learn
        </span>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#2D2926] mb-3" style={{ fontFamily: "Outfit" }}>How LIMYA works</h1>
        <p className="text-[#5C554E] max-w-2xl mb-12">LIMYA is a neighborhood bulletin board for trusted local pros. Here's how to get the most out of it — whether you're looking for help or offering it.</p>

        <h2 className="text-2xl font-bold text-[#2D2926] mb-6" style={{ fontFamily: "Outfit" }}>For neighbors</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-14">
          {NEIGHBOR_STEPS.map(({ icon: Icon, title, desc }, i) => (
            <div key={title} className="bg-white border border-black/[0.07] rounded-2xl p-6 flex items-start gap-4" data-testid={`learn-neighbor-${i}`}>
              <div className="w-11 h-11 rounded-full bg-[#E1EDE7] grid place-items-center shrink-0"><Icon size={20} className="text-[#245544]"/></div>
              <div>
                <p className="font-semibold text-[#2D2926] mb-1" style={{ fontFamily: "Outfit" }}>{title}</p>
                <p className="text-sm text-[#5C554E] leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <h2 className="text-2xl font-bold text-[#2D2926] mb-6" style={{ fontFamily: "Outfit" }}>For pros</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-14">
          {PRO_TIPS.map(({ icon: Icon, title, desc }, i) => (
            <div key={title} className="bg-white border border-black/[0.07] rounded-2xl p-6" data-testid={`learn-pro-${i}`}>
              <div className="w-11 h-11 rounded-full bg-[#E1EDE7] grid place-items-center mb-4"><Icon size={20} className="text-[#245544]"/></div>
              <p className="font-semibold text-[#2D2926] mb-1" style={{ fontFamily: "Outfit" }}>{title}</p>
              <p className="text-sm text-[#5C554E] leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <div className="bg-[#2D2926] text-[#F2F4F2] rounded-3xl p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 justify-between">
          <div>
            <h3 className="text-xl font-bold mb-1.5" style={{ fontFamily: "Outfit" }}>Ready to get started?</h3>
            <p className="text-sm text-[#F2F4F2]/70">Browse pros near you, or pin your own business card to the board.</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Link to="/search" className="px-6 py-3 rounded-full bg-[#E1EDE7] text-[#20493B] font-bold inline-flex items-center gap-2" data-testid="learn-browse-btn">Browse Pros <ArrowRight size={16}/></Link>
            <Link to="/register?role=pro" className="px-6 py-3 rounded-full border border-white/25 text-[#F2F4F2] font-semibold hover:bg-white/10 transition" data-testid="learn-join-btn">Become a Pro</Link>
          </div>
        </div>
      </div>
      <Footer/>
    </div>
  );
}
